import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSnackbar } from 'notistack';
import { useRetention } from '../RetentionContext';
import StageForm from '../components/StageForm';
import MidProgressSources from './MidProgressSources';
import { IconBadge, InfoList, SoftChip } from '../components/ui';
import { EXIT_FIELDS, LEVEL_LABELS, OPTIONS, STAGE_FIELDS } from '../constants/stageFields';
import { RT } from '../theme/retentionTheme';
import {
  getTracking,
  logCallAttempt,
  recordExit,
  remindParent,
  renewEnrollment,
  saveFirstFeedback,
  saveReport,
  shareReport,
} from '../../../redux/features/retentionSlice';

// Stages 8–13 (the Learning Journey tab). Everything comes from GET /retention/:id/tracking, loaded once by the tab.
// 8 first feedback · 9–11 reports (CRO writes on the tutor's behalf until the tutor portal) · 12 call attempts + exits · 13 renewal.

const NO_VALUES = {};
const REPORT_STATUS = {
  0: ['Draft', RT.muted],
  1: ['Submitted', RT.info],
  2: ['Shared', RT.primary],
  3: ['Viewed by parent', RT.success],
};
const label = (options, v) => options.find((o) => String(o.value) === String(v))?.label || '—';

export default function TrackingStage({ stageNo }) {
  const { courseEnrollmentId, refresh } = useRetention();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { tracking, saving } = useSelector((state) => state.retention);
  const [resetKey, setResetKey] = useState(0);
  const [exitType, setExitType] = useState(null);

  const d = tracking.data;
  const cfg = STAGE_FIELDS[stageNo];
  const report = cfg.reportType ? d?.reports?.[cfg.reportType] || null : null;
  const reportStatus = Number(report?.report_status ?? -1);
  const exited = Boolean(d?.exit);
  const renewed = Boolean(d?.renewal?.renewal_date);

  const values = useMemo(() => {
    if (!d) return NO_VALUES;
    if (stageNo === 8) return { ...d.signals, ...(d.firstFeedback || {}) };
    // 9 shows the attendance snapshot, 11 the derived aspiration–achievement gap (both in tracking.signals)
    if (cfg.reportType) return { ...(stageNo === 9 || stageNo === 11 ? d.signals : {}), ...(report || {}) };
    if (stageNo === 13) return { ...d.renewal };
    return NO_VALUES; // 12: every save logs a new attempt
  }, [d, stageNo, cfg.reportType, report]);

  const run = async (action, okMessage) => {
    const res = await dispatch(action);
    const ok = res.payload?.status === 'success';
    enqueueSnackbar(res.payload?.message || (ok ? okMessage : 'Could not save'), { variant: ok ? 'success' : 'error' });
    if (ok) {
      dispatch(getTracking(courseEnrollmentId));
      refresh();
      setResetKey((k) => k + 1);
    }
    return ok;
  };

  const save = (changes) => {
    const base = { courseEnrollmentId, ...changes };
    if (cfg.save === 'firstFeedback') return run(saveFirstFeedback(base), 'Saved');
    if (cfg.save === 'report') return run(saveReport({ ...base, reportType: cfg.reportType }), 'Report saved');
    if (cfg.save === 'callAttempt') return run(logCallAttempt(base), 'Call logged');
    if (cfg.save === 'renew') return run(renewEnrollment(base), 'Renewed');
    return false;
  };

  const readOnly =
    !d ||
    Boolean(tracking.error) ||
    reportStatus >= 2 ||
    (stageNo === 12 && exited) ||
    (stageNo === 13 && (renewed || exited));

  // ---- per-stage extras ----------------------------------------------------------
  let top = null;
  let actions = null;

  if (cfg.reportType) {
    const [statusLabel, statusColor] = REPORT_STATUS[reportStatus] || ['Not written', RT.faint];
    actions = (
      <>
        <SoftChip label={statusLabel} icon="solar:document-text-bold" color={statusColor} />
        {reportStatus === 1 && (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            disabled={saving}
            startIcon={<Icon icon="solar:share-bold" width={16} />}
            onClick={() => run(shareReport(report.retention_progress_report_id), 'Shared')}
          >
            Share with parent
          </Button>
        )}
        {reportStatus === 2 && !report?.parent_viewed_at && (
          <Button
            size="small"
            variant="outlined"
            disabled={saving}
            startIcon={<Icon icon="solar:bell-bing-bold" width={16} />}
            onClick={() => run(remindParent(report.retention_progress_report_id), 'Reminder sent')}
          >
            Remind parent
          </Button>
        )}
      </>
    );
    if (reportStatus >= 2) {
      top = (
        <Alert severity="success" icon={<Icon icon="solar:share-circle-bold" width={20} />}>
          Shared {report?.shared_with_parent_at ? dayjs(report.shared_with_parent_at).format('DD MMM, HH:mm') : ''} — locked for edits.
          {report?.parent_viewed_at ? ` Parent viewed ${dayjs(report.parent_viewed_at).format('DD MMM')}.` : ''}
        </Alert>
      );
    }
    // stages 9 and 11: the tutor writes in the old tables (student_examination, trainer_attendance), shown above the
    // form; saving / sharing stores every pre, mid (Exam N) and post exam with the report
    if ((stageNo === 9 || stageNo === 11) && d) {
      top = (
        <Stack gap={1.5}>
          {top}
          <MidProgressSources subjects={d.mid?.subjects} stored={report?.exams} />
        </Stack>
      );
    }
  }

  if (stageNo === 8 && d?.reports?.[4]) {
    const c = d.reports[4];
    top = (
      <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#F7F0FF', border: `1px solid ${RT.line}` }}>
        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          <IconBadge icon="solar:square-academic-cap-bold-duotone" color="#7C3AED" size={28} />
          <Typography variant="subtitle2">Tutor check-in</Typography>
          <SoftChip label="internal" icon="solar:lock-keyhole-bold" color="#7C3AED" sx={{ height: 20, fontSize: 10.5 }} />
          {Number(c.needs_cro_support) === 1 && <SoftChip label="Needs CRO help" icon="solar:danger-bold" color={RT.error} />}
        </Stack>
        <InfoList
          rows={[
            { label: 'Engagement', value: c.student_engagement_rating ? LEVEL_LABELS[c.student_engagement_rating - 1] : null },
            { label: 'Punctuality', value: c.student_punctuality_rating ? LEVEL_LABELS[c.student_punctuality_rating - 1] : null },
            { label: 'vs demo', value: label(OPTIONS.understanding, c.understanding_vs_demo) },
            { label: 'Concerns', value: c.improvement_areas },
          ]}
        />
      </Box>
    );
  }

  if (stageNo === 12) {
    const calls = d?.calls || [];
    top = (
      <Stack gap={1.25}>
        {calls.length > 0 && (
          <Stack gap={0.75}>
            {calls.map((c) => (
              <Stack
                key={c.retention_parent_touchpoint_id}
                direction="row"
                alignItems="center"
                gap={1.25}
                sx={{ p: 1, borderRadius: 2, border: `1px solid ${RT.line}` }}
              >
                <IconBadge
                  icon={Number(c.connected) === 1 ? 'solar:phone-calling-bold' : 'solar:phone-rounded-linear'}
                  color={Number(c.connected) === 1 ? RT.success : RT.warning}
                  size={30}
                />
                <Typography variant="subtitle2" sx={{ minWidth: 28 }}>
                  #{c.call_attempts}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {Number(c.connected) === 1
                    ? `${label(OPTIONS.sentiment, c.parent_sentiment)} · intent ${label(OPTIONS.renewalIntent, c.renewal_intent)}`
                    : 'No answer'}
                  {c.qualitative_notes ? ` · ${c.qualitative_notes}` : ''}
                </Typography>
                <Typography variant="caption" sx={{ color: RT.muted }}>
                  {c.last_attempt_at ? dayjs(c.last_attempt_at).format('DD MMM, HH:mm') : ''}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
        {exited ? (
          <Alert severity={Number(d.exit.touchpoint_type) === 3 ? 'error' : 'warning'} icon={<Icon icon="solar:logout-3-bold" width={20} />}>
            Exit recorded · {EXIT_FIELDS[d.exit.touchpoint_type]?.title} · enrolment inactive.
          </Alert>
        ) : null}
      </Stack>
    );
    if (!exited) {
      actions = (
        <>
          <Button size="small" variant="outlined" color="error" startIcon={<Icon icon="solar:sad-circle-bold" width={16} />} onClick={() => setExitType(3)}>
            Unhappy exit
          </Button>
          <Button size="small" variant="outlined" color="warning" startIcon={<Icon icon="solar:smile-circle-bold" width={16} />} onClick={() => setExitType(4)}>
            Happy, leaving
          </Button>
        </>
      );
    }
  }

  if (stageNo === 13 && d?.renewal) {
    if (!d.renewal.renew_supported) {
      top = (
        <Alert severity="warning" icon={<Icon icon="solar:danger-triangle-bold" width={20} />}>
          AED-priced course: extra sessions and amount need the course converted to INR first (Cart › Convert to INR). The renewal details
          still save.
        </Alert>
      );
    }
    if (renewed) {
      top = (
        <Alert severity="success" icon={<Icon icon="solar:cup-star-bold" width={20} />}>
          Renewed on {dayjs(d.renewal.renewal_date).format('DD MMM YYYY')}
          {d.renewal.last ? ` · +${d.renewal.last.extra_sessions} sessions · ${d.renewal.currency} ${d.renewal.last.extra_sale_amount}` : ''}.
        </Alert>
      );
    }
  }

  return (
    <Stack gap={2}>
      {top}
      <StageForm
        stageNo={stageNo}
        values={values}
        prefill={stageNo === 9 ? d?.mid?.prefill : undefined}
        dynamicOptions={d?.options}
        onSave={save}
        saving={saving}
        readOnly={readOnly}
        extraActions={actions}
        resetKey={resetKey}
        readOnlyLabel={
          reportStatus >= 2 ? 'Shared · locked' : stageNo >= 12 && exited ? 'Exit recorded · locked' : stageNo === 13 && renewed ? 'Renewed · locked' : undefined
        }
      />

      <Dialog open={Boolean(exitType)} onClose={() => setExitType(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        {exitType && (
          <DialogContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" gap={1.5} mb={2}>
              <IconBadge icon={EXIT_FIELDS[exitType].icon} color={exitType === 3 ? RT.error : RT.warning} size={44} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">{EXIT_FIELDS[exitType].title}</Typography>
                <Typography variant="caption" sx={{ color: RT.muted }}>
                  Closes the enrolment (inactive) and unmaps the tutor
                </Typography>
              </Box>
              <IconButton onClick={() => setExitType(null)}>
                <Icon icon="solar:close-circle-linear" width={22} />
              </IconButton>
            </Stack>
            <StageForm
              stageNo={12}
              config={EXIT_FIELDS[exitType]}
              dynamicOptions={d?.options}
              saving={saving}
              saveLabel="Record exit"
              onSave={async (changes) => {
                const ok = await run(recordExit({ courseEnrollmentId, exit_type: exitType, ...changes }), 'Exit recorded');
                if (ok) setExitType(null);
              }}
            />
          </DialogContent>
        )}
      </Dialog>
    </Stack>
  );
}
